/**
 * Upgrade Banner
 *
 * Shown in local mode to encourage upgrade to cloud studio.
 * Dismissible for 7 days. Shows at meaningful moments:
 * - Dashboard (always, unless dismissed)
 * - MyStories (when trying to create Story #2)
 * - WriteStory (after completing a story)
 */

import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { X, Cloud, Sparkles, BookOpen } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '@/components/ui/Button'
import { useStudio } from '@/context/StudioContext'
import { LOCAL_STORAGE_KEYS } from '@/types/studio'

const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000 // 7 days

interface UpgradeBannerProps {
  /** Banner variant */
  variant?: 'default' | 'story-limit' | 'after-completion'
  /** Optional callback when dismissed */
  onDismiss?: () => void
}

export function UpgradeBanner({ variant = 'default', onDismiss }: UpgradeBannerProps) {
  const { mode } = useStudio()
  const [isDismissed, setIsDismissed] = useState(true)

  useEffect(() => {
    // Compute the dismissed state based on current conditions
    const computeDismissed = (): boolean => {
      // Only show in local mode
      if (mode === 'cloud') return true

      // Special variants always show (they're contextual)
      if (variant === 'story-limit' || variant === 'after-completion') return false

      // Check if default banner was previously dismissed
      const dismissedAt = localStorage.getItem(LOCAL_STORAGE_KEYS.UPGRADE_BANNER_DISMISSED)
      if (dismissedAt) {
        const dismissedTime = parseInt(dismissedAt, 10)
        if (Date.now() - dismissedTime < DISMISS_DURATION_MS) return true
      }

      return false
    }

    // Defer state update to avoid sync setState warning
    const timer = setTimeout(() => setIsDismissed(computeDismissed()), 0)
    return () => clearTimeout(timer)
  }, [mode, variant])

  const handleDismiss = () => {
    if (variant === 'default') {
      localStorage.setItem(LOCAL_STORAGE_KEYS.UPGRADE_BANNER_DISMISSED, Date.now().toString())
    }
    setIsDismissed(true)
    onDismiss?.()
  }

  if (isDismissed || mode === 'cloud') return null

  // Story limit variant - shown when trying to create a second story
  if (variant === 'story-limit') {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-[var(--radius-kid)] p-6 text-white"
        >
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <BookOpen className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-display font-bold text-lg mb-1">
                  One Story at a Time
                </h3>
                <p className="text-white/90 text-sm">
                  Free accounts can have one active story. Upgrade to Cloud Studio for unlimited stories!
                </p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="text-white/60 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="mt-4 flex gap-3">
            <Link to="/upgrade">
              <Button variant="secondary" size="sm" className="bg-white text-orange-600 hover:bg-white/90">
                <Sparkles className="w-4 h-4 mr-2" />
                Upgrade Now
              </Button>
            </Link>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDismiss}
              className="text-white/80 hover:text-white hover:bg-white/10"
            >
              Maybe Later
            </Button>
          </div>
        </motion.div>
      </AnimatePresence>
    )
  }

  // After completion variant - shown after completing a story
  if (variant === 'after-completion') {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="bg-gradient-to-r from-purple-500 to-indigo-600 rounded-[var(--radius-kid)] p-6 text-white"
        >
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <Sparkles className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-display font-bold text-lg mb-1">
                  Ready for Story #2?
                </h3>
                <p className="text-white/90 text-sm">
                  Great work! Upgrade to Cloud Studio for unlimited stories, progress tracking, and badges.
                </p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="text-white/60 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="mt-4 flex gap-3">
            <Link to="/upgrade">
              <Button variant="secondary" size="sm" className="bg-white text-purple-600 hover:bg-white/90">
                <Sparkles className="w-4 h-4 mr-2" />
                Upgrade Now
              </Button>
            </Link>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDismiss}
              className="text-white/80 hover:text-white hover:bg-white/10"
            >
              Maybe Later
            </Button>
          </div>
        </motion.div>
      </AnimatePresence>
    )
  }

  // Default banner - subtle, shown at top of pages
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: 'auto' }}
        exit={{ opacity: 0, height: 0 }}
        className="bg-[var(--color-kids-surface-alt)] border-b border-[var(--color-kids-border)]"
      >
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Cloud className="w-5 h-5 text-[var(--color-kids-primary)]" />
            <span className="text-sm text-[var(--color-kids-text)]">
              <strong>Local Studio</strong> — Your story is saved on this device only.{' '}
              <Link
                to="/upgrade"
                className="text-[var(--color-kids-primary)] hover:underline font-medium"
              >
                Upgrade to Cloud Studio
              </Link>
            </span>
          </div>
          <button
            onClick={handleDismiss}
            className="text-[var(--color-kids-text-muted)] hover:text-[var(--color-kids-text)] transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  )
}
